/**
 * TEF Canada (D-109): the pure logic shared by the web and the mobile app — the sitting sent by
 * the server (a CO document at a time, heard once; CE free; EE and EO sections A and B), the
 * results (score out of 699, NCLC from the official table, always an estimate), the flow of a CO
 * document (reading time, one listening, answer time) and the section timers of the written
 * expression. Every number comes from the versioned format table (`GET academy/format/?exam=
 * tef_canada`): nothing about the exam is written here. No React and no platform API.
 */

import { isObject, num, numOrNull, objects, str } from "../ielts/read";
import { type IeltsState, parseIeltsState } from "../ielts/sitting";
import { type SpokenLine, parseSpokenLines } from "../listening";

export const TEF_EXAM = "tef_canada";
export type TefSkill = "co" | "ce" | "ee" | "eo";
export const TEF_SKILLS: readonly TefSkill[] = ["co", "ce", "ee", "eo"];
/** The TEF sections of EE and EO are stored as tasks 1 (A) and 2 (B). */
export type TefSection = "A" | "B";

export function sectionLetter(number: number): TefSection | "" {
  return number === 1 ? "A" : number === 2 ? "B" : "";
}

// --- Format table (data) ----------------------------------------------------------------------

export interface TefPart {
  section: number;
  key: string;
  questions: number;
  label: { fr: string; en: string };
}

export interface TefFormat {
  scaleMax: number;
  /** [NCLC, minimum score] per ability, highest first (official table, from 11/12/2023). */
  nclc: Record<TefSkill, [number, number][]>;
  parts: Record<"co" | "ce", TefPart[]>;
  flow: { readingSeconds: number; answerSeconds: number };
  sections: Record<"ee" | "eo", Record<string, { minutes: number; seconds: number; minWords: number | null }>>;
  /** The platform's estimate: share of the grid points (0 to 1) → score out of 699, per skill. */
  productionAnchors: Record<"ee" | "eo", [number, number][]>;
}

const ABILITY: Record<TefSkill, string> = { co: "listening", ce: "reading", ee: "writing", eo: "speaking" };

function pairs(value: unknown): [number, number][] {
  return (Array.isArray(value) ? value : [])
    .filter((row): row is unknown[] => Array.isArray(row) && row.length === 2)
    .map((row) => [num(row[0]), num(row[1])] as [number, number])
    .sort((a, b) => b[1] - a[1]);
}

function anchors(value: unknown): [number, number][] {
  return (Array.isArray(value) ? value : [])
    .filter((row): row is unknown[] => Array.isArray(row) && row.length === 2)
    .map((row) => [num(row[0]), num(row[1])] as [number, number]);
}

function parts(value: unknown): TefPart[] {
  return objects(value).map((p) => {
    const label = isObject(p.label) ? p.label : {};
    return { section: num(p.section), key: str(p.key), questions: num(p.questions), label: { fr: str(label.fr), en: str(label.en) } };
  });
}

/** The format endpoint's answer (`sections`, `details`) read defensively. */
export function parseTefFormat(value: unknown): TefFormat {
  const data = isObject(value) ? value : {};
  const sections = isObject(data.sections) ? data.sections : {};
  const details = isObject(data.details) ? data.details : {};
  const table = isObject(details.nclc) ? details.nclc : {};
  const scale = isObject(details.scale) ? details.scale : {};
  const production = isObject(details.production_conversion) ? details.production_conversion : {};
  const section = (key: string) => (isObject(sections[key]) ? (sections[key] as Record<string, unknown>) : {});
  const co = section("co");
  const flow = isObject(co.flow) ? co.flow : {};
  const tasks = (key: string) => {
    const raw = isObject(section(key).tasks) ? (section(key).tasks as Record<string, unknown>) : {};
    const out: Record<string, { minutes: number; seconds: number; minWords: number | null }> = {};
    for (const [n, task] of Object.entries(raw)) {
      const t = isObject(task) ? task : {};
      out[n] = { minutes: num(t.minutes), seconds: num(t.seconds), minWords: numOrNull(t.min_words) };
    }
    return out;
  };
  return {
    scaleMax: num(scale.max, 699),
    nclc: {
      co: pairs(table[ABILITY.co]),
      ce: pairs(table[ABILITY.ce]),
      ee: pairs(table[ABILITY.ee]),
      eo: pairs(table[ABILITY.eo]),
    },
    parts: { co: parts(co.parts), ce: parts(section("ce").parts) },
    flow: { readingSeconds: num(flow.reading_seconds), answerSeconds: num(flow.answer_seconds) },
    sections: { ee: tasks("ee"), eo: tasks("eo") },
    productionAnchors: { ee: anchors(production.ee), eo: anchors(production.eo) },
  };
}

/** The estimated score out of 699 of a section (A or B) from its grid points, as the server does. */
export function sectionEstimate(skill: "ee" | "eo", points: number, max: number, fmt: TefFormat): number {
  const share = max > 0 ? Math.min(Math.max(points / max, 0), 1) : 0;
  return Math.min(interpolate(share, fmt.productionAnchors[skill]), fmt.scaleMax);
}

/** Score out of 699 → NCLC with the table (0 under the lowest level), as the server does. */
export function nclcFor(skill: TefSkill, score: number, table: TefFormat["nclc"]): number {
  for (const [level, minimum] of table[skill] ?? []) if (score >= minimum) return level;
  return 0;
}

/** The score needed for an NCLC in a skill (null when the table has no such level). */
export function scoreForNclc(skill: TefSkill, level: number, table: TefFormat["nclc"]): number | null {
  const row = (table[skill] ?? []).find(([n]) => n === level);
  return row ? row[1] : null;
}

/**
 * Linear interpolation between anchor points [x, score] (the platform's estimate of the
 * conversion, half up), like `tef._interpolate`.
 */
export function interpolate(value: number, anchors: [number, number][]): number {
  const points = [...anchors].sort((a, b) => a[0] - b[0]);
  if (!points.length) return 0;
  const first = points[0] as [number, number];
  if (value <= first[0]) return first[1];
  for (let i = 1; i < points.length; i += 1) {
    const [x0, y0] = points[i - 1] as [number, number];
    const [x1, y1] = points[i] as [number, number];
    if (value <= x1) return Math.floor(y0 + (y1 - y0) * (x1 === x0 ? 0 : (value - x0) / (x1 - x0)) + 0.5);
  }
  return (points[points.length - 1] as [number, number])[1];
}

/** The CO / CE section (1 to 7, 1 to 5) of a question number, from the table's parts. */
export function sectionOfNumber(number: number, list: TefPart[]): TefPart | null {
  let end = 0;
  for (const part of [...list].sort((a, b) => a.section - b.section)) {
    end += part.questions;
    if (number <= end) return part;
  }
  return null;
}

// --- Sitting -----------------------------------------------------------------------------------

export interface TefDocumentInfo {
  type: string;
  level: string;
  /** The lines read by the device's voices when there is no audio (labels never read). */
  lines: SpokenLine[];
}

export interface TefTaskInfo {
  id: string;
  section: TefSection | "";
  /** The minutes of the section (EE, format table) and its seconds (EO). */
  minutes: number;
  seconds: number;
  documents: { title: string; text: string }[];
  role: string;
  disagrees: boolean;
  turns: { role: string; text: string }[];
}

export interface TefState extends IeltsState {
  /** CO: the type, level and spoken lines (labels never read) of the current document. */
  document: TefDocumentInfo | null;
  flow: { readingSeconds: number; answerSeconds: number };
  /** CO: the official instruction of the current document's section, shown on screen (D-135). */
  instruction: string;
  tefTasks: TefTaskInfo[];
}

export function parseTefState(value: unknown): TefState {
  const base = parseIeltsState(value);
  const data = isObject(value) ? value : {};
  const current = isObject(data.current) && isObject(data.current.passage) ? data.current.passage : null;
  const flow = isObject(data.flow) ? data.flow : {};
  return {
    ...base,
    document: current ? { type: str(current.type), level: str(current.level), lines: parseSpokenLines(current.lines) } : null,
    flow: { readingSeconds: num(flow.reading_seconds), answerSeconds: num(flow.answer_seconds) },
    instruction: isObject(data.instruction) ? str(data.instruction.fr) : "",
    tefTasks: objects(data.tasks).map((task) => {
      const examiner = isObject(task.examiner) ? task.examiner : {};
      const limits = isObject(task.limits) ? task.limits : {};
      return {
        minutes: num(limits.minutes),
        seconds: num(limits.seconds),
        id: str(task.id),
        section: str(task.section) === "B" ? "B" : str(task.section) === "A" ? "A" : sectionLetter(num(task.number)),
        documents: objects(task.documents).map((d) => ({ title: str(d.title), text: str(d.text) })),
        role: str(examiner.role),
        disagrees: examiner.disagrees === true,
        turns: objects(examiner.turns)
          .map((t) => ({ role: str(t.role), text: str(t.text) }))
          .filter((t) => t.text),
      };
    }),
  };
}

export type ListeningPhase = "reading" | "listening" | "answering" | "closed";

/**
 * The flow of one CO document, as at the centre: time to read the questions, the document once
 * (its length is known only while it plays: `listened` says it ended), then the answer time.
 */
export function listeningPhase(
  elapsedSeconds: number,
  flow: { readingSeconds: number; answerSeconds: number },
  listened: { started: boolean; endedAt: number | null },
): ListeningPhase {
  if (!listened.started) return elapsedSeconds < flow.readingSeconds ? "reading" : "listening";
  if (listened.endedAt === null) return "listening";
  return elapsedSeconds - listened.endedAt < flow.answerSeconds ? "answering" : "closed";
}

/**
 * Written expression: 25 minutes for section A, then 35 for section B (one hour in all). The
 * section the learner should be on and the minutes left in it, from the time spent.
 */
export function writingPlan(
  elapsedSeconds: number,
  minutes: { A: number; B: number },
): { section: TefSection; left: number; overA: boolean } {
  const a = minutes.A * 60;
  if (elapsedSeconds < a) return { section: "A", left: Math.ceil((a - elapsedSeconds) / 60), overA: false };
  const b = a + minutes.B * 60;
  return { section: "B", left: Math.max(0, Math.ceil((b - elapsedSeconds) / 60)), overA: true };
}

/** Section A: the questions asked by the learner (a line with « ? » counts as one question). */
export function questionsAsked(turns: { role: string; text: string }[]): number {
  return turns.filter((t) => t.role === "learner").reduce((n, t) => n + (t.text.match(/\?/g)?.length ?? 0), 0);
}

// --- Results -----------------------------------------------------------------------------------

export interface TefSkillResult {
  score: number | null;
  nclc: number | null;
  cefr: string;
  right: number | null;
  questions: number | null;
  pending: boolean;
  /** D-171: planned tasks left empty (they count at the lowest score), out of the planned tasks. */
  unanswered: number;
  tasksPlanned: number;
  sections: Record<string, number | { right: number; questions: number }>;
  /** D-163: the « Équivalence ancien score » range of the estimated NCLC (IRCC table), or null. */
  oldScore: { min: number | null; max: number | null } | null;
}

/** « 349–370 », « 393 + » (top row), « < 181 » (under NCLC 4), or "" when unknown. */
export function oldScoreText(range: { min: number | null; max: number | null } | null): string {
  if (!range) return "";
  if (range.min !== null && range.max !== null) return `${range.min}–${range.max}`;
  if (range.min !== null) return `${range.min} +`;
  if (range.max !== null) return `< ${range.max + 1}`;
  return "";
}

export interface TefResults {
  skills: Partial<Record<TefSkill, TefSkillResult>>;
  nclcMin: number | null;
  placement: { level: string; nclc: number | null; cefr: string; beginner: boolean } | null;
}

export function parseTefResults(value: unknown): TefResults {
  const data = isObject(value) ? value : {};
  const skills: Partial<Record<TefSkill, TefSkillResult>> = {};
  for (const skill of TEF_SKILLS) {
    const row = data[skill];
    if (!isObject(row)) continue;
    const sections: TefSkillResult["sections"] = {};
    if (isObject(row.sections)) {
      for (const [key, v] of Object.entries(row.sections)) {
        if (typeof v === "number") sections[key] = v;
        else if (isObject(v)) sections[key] = { right: num(v.right), questions: num(v.questions) };
      }
    }
    skills[skill] = {
      score: numOrNull(row.score),
      nclc: numOrNull(row.nclc),
      cefr: str(row.cefr),
      right: numOrNull(row.right),
      questions: numOrNull(row.questions),
      pending: row.pending === true,
      unanswered: num(row.unanswered),
      tasksPlanned: num(row.tasks_planned),
      sections,
      oldScore: isObject(row.old_score) ? { min: numOrNull(row.old_score.min), max: numOrNull(row.old_score.max) } : null,
    };
  }
  const placement = isObject(data.placement)
    ? {
        level: str(data.placement.level),
        nclc: numOrNull(data.placement.nclc),
        cefr: str(data.placement.cefr),
        beginner: data.placement.beginner === true,
      }
    : null;
  return { skills, nclcMin: isObject(data.overall) ? numOrNull(data.overall.nclc_min) : null, placement };
}

/** The lowest NCLC of the four abilities (what limits an Express Entry file), or null. */
export function lowestNclc(results: TefResults): number | null {
  const values = TEF_SKILLS.map((s) => results.skills[s]?.nclc);
  return values.every((v): v is number => typeof v === "number") ? Math.min(...values) : null;
}

/** « 434 / 699 », or « — » while not measured. */
export function formatScore(score: number | null | undefined, max = 699): string {
  return typeof score === "number" && Number.isFinite(score) ? `${Math.round(score)} / ${max}` : "—";
}

/** Gap to the goal per ability (0 when reached, null when not measured). */
export function gaps(results: TefResults, target: number): Record<TefSkill, number | null> {
  const out = {} as Record<TefSkill, number | null>;
  for (const skill of TEF_SKILLS) {
    const level = results.skills[skill]?.nclc;
    out[skill] = typeof level === "number" ? Math.max(target - level, 0) : null;
  }
  return out;
}

/**
 * « How to succeed in this type of question? » for the TEF: CO and CE by the type of document
 * (the section key), EE and EO by the section (task 1 = A, 2 = B).
 */
export function tefMethodQuery(skill: TefSkill, detail: { type?: string; task?: number }): Record<string, string> {
  if (skill === "co" || skill === "ce") return { exam: TEF_EXAM, skill, ...(detail.type ? { type: detail.type } : {}) };
  return { exam: TEF_EXAM, skill, ...(detail.task ? { task: String(detail.task) } : {}) };
}

/** The review rows of a TEF sitting that carry the English help of the explanation. */
export function reviewHelp(value: unknown): Record<string, { en: string; type: string }> {
  const data = isObject(value) ? value : {};
  const out: Record<string, { en: string; type: string }> = {};
  objects(data.questions).forEach((q, index) => {
    out[str(q.id) || `r${index}`] = { en: str(q.explanation_en), type: str(q.section_type) };
  });
  return out;
}

